import React, { useState, useRef, useEffect } from 'react';
import { useStore } from 'zustand';
import { useMutation } from '@tanstack/react-query';
import { Honeybadger } from '@honeybadger-io/react';
import ReactCrop, { convertToPixelCrop } from 'react-image-crop';
import imageCompression from 'browser-image-compression';

import toast from 'react-hot-toast';

import { centerAspectCrop } from './lib';
import { authorisedUploadImage } from '../lib';

import TextInput from '../../inputs/form-inputs/TextInput';

const aspectRatios = [
  { label: 'Freeform', value: undefined },
  { label: '1:1', value: 1 },
  { label: '16:9', value: 16 / 9 },
  { label: '9:16', value: 1.5 },
  { label: '4:3', value: 4 / 3 },
  { label: '3:4', value: 0.75 },
];

export default function ImageCropperInput({ isStandAlone, imageStore, endpoints, handleCallback }) {
  const imgRef = useRef(null);

  const [isSaving, setIsSaving] = useState(false);
  const [crop, setCrop] = useState({ x: 0, y: 0, width: 0, height: 0, unit: '%' });
  const [pixelCrop, setPixelCrop] = useState({ x: 0, y: 0, width: 0, height: 0, unit: 'px' });
  const [scale, setScale] = useState(1);
  const [rotate, setRotate] = useState(0);
  const [aspect, setAspect] = useState(undefined); // Either a number or undefined

  const uploadImageMutation = useMutation({
    mutationFn: async ({ base64Data, filename }) => {
      const res = await authorisedUploadImage(base64Data, filename, endpoints);
      if (!res.image) {
        throw new Error(JSON.stringify(res, null, 2));
      }
      return res.image;
    },
    onError: (error) => {
      toast.error(
        <div className="d-flex flex-column gap-2 align-items-center justify-content-center">
          <b className="m-0">Error uploading image</b>
          <pre style={{ margin: 0, whiteSpace: 'pre-wrap' }}>{error.message}</pre>
        </div>,
      );
      Honeybadger.notify(error);
    },
    onSuccess: (image) => {
      if (handleCallback) {
        handleCallback(image);
        return;
      }

      window.location.href = endpoints.adminImagesUrl;
    },
    onSettled: () => setIsSaving(false),
  });

  const filename = useStore(imageStore, (state) => state.filename);
  const fileType = useStore(imageStore, (state) => state.fileType);
  const originalFilename = useStore(imageStore, (state) => state.originalFilename);
  const dataUrl = useStore(imageStore, (state) => state.dataUrl);
  const setFilename = useStore(imageStore, (state) => state.setFilename);
  const setImageStore = useStore(imageStore, (state) => state.setImageStore);

  const handleCropOnChange = (_, percentCrop) => {
    setCrop(percentCrop);
  };

  const setNaturalSize = () => {
    const crop = {
      x: 0,
      y: 0,
      width: 100,
      height: 100,
      unit: '%',
    };

    setCrop(crop);
  };

  const onImageLoad = (e) => {
    const { naturalWidth, naturalHeight } = e.currentTarget;

    if (aspect) {
      const crop = centerAspectCrop(naturalWidth, naturalHeight, aspect);
      setCrop(crop);
      return;
    }

    setNaturalSize();
  };

  const handleReset = () => {
    setCrop(null);
    setPixelCrop({ x: 0, y: 0, width: 0, height: 0, unit: 'px' });
    setScale(1);
    setRotate(0);
    setAspect(undefined);
  };

  const handleMaximise = () => {
    setNaturalSize();
    setScale(1);
    setRotate(0);
    setAspect(undefined);
  };

  const handleToggleAspectOnClick = (aspect) => {
    setAspect(aspect);

    if (imgRef.current) {
      const { naturalWidth, naturalHeight } = imgRef.current;

      if (aspect === undefined) {
        setNaturalSize();
        return;
      }

      const newCrop = centerAspectCrop(naturalWidth, naturalHeight, aspect);
      setCrop(newCrop);
    }
  };

  const handleSave = async () => {
    try {
      setIsSaving(true);

      const image = imgRef.current;
      if (!image) {
        toast.error('Image not loaded. Please ensure the image is loaded before saving.');
        return;
      }

      // Convert crop from % to px
      const naturalWidth = image.naturalWidth;
      const naturalHeight = image.naturalHeight;

      const cropX = (crop.x / 100) * naturalWidth;
      const cropY = (crop.y / 100) * naturalHeight;
      const cropWidth = (crop.width / 100) * naturalWidth;
      const cropHeight = (crop.height / 100) * naturalHeight;

      const canvas = document.createElement('canvas');
      canvas.width = cropWidth;
      canvas.height = cropHeight;

      const ctx = canvas.getContext('2d');
      if (!ctx) {
        throw new Error('No 2d context');
      }

      ctx.drawImage(image, cropX, cropY, cropWidth, cropHeight, 0, 0, cropWidth, cropHeight);

      canvas.toBlob(async (blob) => {
        if (!blob) {
          toast.error('Failed to create image blob.');
          return;
        }

        // Compress the blob using browser-image-compression
        const options = {
          maxSizeMB: 5,
          useWebWorker: true,
          fileType,
          initialQuality: fileType === 'image/jpeg' ? 0.8 : undefined,
        };

        try {
          const compressedBlob = await imageCompression(blob, options);

          // Convert the compressed blob to base64
          const reader = new FileReader();
          reader.readAsDataURL(compressedBlob);
          reader.onloadend = () => {
            try {
              const base64Data = reader.result;
              uploadImageMutation.mutate({ base64Data: base64Data.split(',')[1], filename });
            } catch (err) {
              toast.error('Failed to process image to base64Data for upload: ' + err.message);
            }
          };
          reader.onerror = (err) => {
            toast.error('Failed to read image blob: ' + err.message);
          };
        } catch (err) {
          throw new Error('Image compression failed: ' + err.message);
        }
      }, fileType);
    } catch (error) {
      Honeybadger.notify(error);
      toast.error(error.message || 'An error occurred while attempting to save the cropped image.');
    }
  };

  const isFileNameEmpty = filename === '';

  const isInvalidFilename = isFileNameEmpty || originalFilename === filename;

  const isInvalidCrop = !crop || crop.width === 0 || crop.height === 0;

  useEffect(() => {
    if (imgRef.current && crop) {
      const { naturalWidth, naturalHeight } = imgRef.current;
      const pixelCrop = convertToPixelCrop(crop, naturalWidth, naturalHeight);
      setPixelCrop(pixelCrop);
    }
  }, [crop]);

  return (
    <div className="react-cropper-parent-container">
      <div className="d-flex flex-column gap-2 w-100">
        <div className="input-group">
          <span className="input-group-text">Filename</span>
          <TextInput {...{ value: filename || '', onChange: (e) => setFilename(e.target.value), placeholder: 'Enter image name here...' }} />
        </div>
        {isInvalidFilename && <div className="text-danger">Please enter a valid filename. It cannot be empty or the same as the original filename.</div>}
        {isInvalidCrop && <div className="text-danger">Please select a valid crop area before saving.</div>}
      </div>
      <div className="d-flex justify-content-between w-100">
        <button className="btn btn-secondary" onClick={() => setImageStore({ page: 0, dataUrl: '', filename: '' })}>
          <i className="fe fe-arrow-left" />
        </button>
        <div className="d-flex gap-3 align-items-center">
          <button className="btn btn-outline" onClick={handleReset}>
            Reset
          </button>
          <button className="btn btn-outline" onClick={handleMaximise}>
            Maximise
          </button>
          {aspectRatios.map((a) => (
            <React.Fragment key={a.label}>
              <input
                type="radio"
                className="btn-check"
                name="options-aspect"
                id={`option-${a.label}`}
                autoComplete="off"
                checked={aspect === a.value}
                onChange={() => handleToggleAspectOnClick(a.value)}
              />
              <label className="btn" htmlFor={`option-${a.label}`}>
                {a.label}
              </label>
            </React.Fragment>
          ))}
        </div>
        <button className="btn btn-primary" onClick={handleSave} disabled={isSaving || isInvalidCrop || isInvalidFilename}>
          {isSaving ? (
            <div className="spinner-border spinner-border-sm" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          ) : (
            <span>Save</span>
          )}
        </button>
      </div>
      <div className="d-flex justify-content-between w-100">
        <span className="text-muted">
          W:{pixelCrop.width.toFixed(0)}px x H:{pixelCrop.height.toFixed(0)}px
        </span>
      </div>
      <div className="react-cropper-container">
        <ReactCrop
          className="react-cropper"
          keepSelection
          ruleOfThirds
          crop={crop}
          aspect={aspect}
          onChange={handleCropOnChange}
          onComplete={handleCropOnChange}
        >
          <img
            ref={imgRef}
            src={dataUrl}
            onLoad={onImageLoad}
            crossOrigin="anonymous"
            style={{
              maxHeight: '500px',
              maxWidth: '100%',
              width: 'auto',
              height: 'auto',
              objectFit: 'contain',
              display: 'block',
              transform: `scale(${scale}) rotate(${rotate}deg)`,
            }}
          />
        </ReactCrop>
      </div>
    </div>
  );
}
